// ONBW1-S1 (docs/build-briefs/ONBW1-S1-welcome-link.md; DEC-416; design/mockups/onboarding-wizard/b0-1-welcome and
// b0-3-pin): the pure half of the welcome screens a new owner sees on the device. No DOM here, so a plain node:test
// file can import it. ui/src/welcomeLink.ts draws it.

/** The server's token: 32 random bytes as 43 base64url characters. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * The token in the address a welcome link opens, or undefined. The link is `<origin>/#welcome=<token>`: a fragment is
 * never sent to a server, a proxy or a Referer, and it is not a query string. Anything that is not exactly that shape is
 * not a welcome link and the page carries on as it always did.
 */
export function parseWelcomeFragment(hash: string): string | undefined {
  const prefix = "#welcome=";
  if (!hash.startsWith(prefix)) return undefined;
  const token = hash.slice(prefix.length);
  return TOKEN_PATTERN.test(token) ? token : undefined;
}

/** "Welcome, Dana": the first word of the owner's name. */
export function welcomeHeading(ownerName: string): string {
  const first = ownerName.trim().split(/\s+/)[0] ?? "";
  return first === "" ? "Welcome" : `Welcome, ${first}`;
}

export const WELCOME_CONNECTED_TEXT =
  "Connected. About 25 minutes to set up. You can stop any time.";

/** "6 numbers" when the location's policy is one length, "4 to 6 numbers" when it is a range. */
export function pinLengthText(minLength: number, maxLength: number): string {
  return minLength === maxLength ? `${maxLength} numbers` : `${minLength} to ${maxLength} numbers`;
}

export function pinHintText(minLength: number, maxLength: number): string {
  return `${pinLengthText(minLength, maxLength)}. You use it to sign in every day.`;
}

export function pinNoteText(minLength: number, maxLength: number): string {
  return `Enter ${pinLengthText(minLength, maxLength)}`;
}

/** Adds one digit; a key that is not a digit, or a digit past the location's longest PIN, changes nothing. */
export function appendPinDigit(digits: string, key: string, maxLength: number): string {
  if (!/^\d$/.test(key) || digits.length >= maxLength) return digits;
  return digits + key;
}

export function deletePinDigit(digits: string): string {
  return digits.slice(0, -1);
}

/** Save PIN is on once the PIN is at least the location's shortest. */
export function canSavePin(digits: string, minLength: number): boolean {
  return digits.length >= minLength;
}

/** The dots: as many as the location's longest PIN, the entered ones filled. */
export function pinDots(digits: string, maxLength: number): readonly boolean[] {
  return Array.from({ length: maxLength }, (_, index) => index < digits.length);
}

/**
 * Plain words for a PIN the server refused. The server's own message is for the log; a person is told the rule. A
 * stale or spent link (409) and a lost device credential (401) are not about the PIN and say so.
 */
export function describePinFailure(
  status: number,
  message: string | undefined,
  minLength: number,
  maxLength: number,
): string {
  if (status === 400) {
    if (message !== undefined && /too easy to guess/i.test(message)) {
      return "That PIN is too easy to guess. Choose a different PIN.";
    }
    return `Use ${pinLengthText(minLength, maxLength)}.`;
  }
  if (status === 409 || status === 401) {
    return "This link has already been used. Ask Snip & Sip for a new one.";
  }
  return "That did not save. Try again.";
}

// ---------------------------------------------------------------------------------------------------------------
// The open attempt. The server binds the device to the secret the device sends, and answers a repeat with the same
// secret as a replay. So a response that never arrived is harmless only if the retry presents the SAME secret: the
// secret is kept in sessionStorage (this tab only, gone when it closes) for the attempt, with the token it is for, so a
// "Try again" or a reload resumes it. Cleared as soon as the server has answered at all.

export const WELCOME_ATTEMPT_KEY = "bms-welcome-attempt";

export interface AttemptStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface Attempt {
  token: string;
  secret: string;
}

function readAttempt(store: AttemptStore): Attempt | undefined {
  try {
    const raw = store.getItem(WELCOME_ATTEMPT_KEY);
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as Partial<Attempt>;
    return typeof parsed.token === "string" && typeof parsed.secret === "string"
      ? { token: parsed.token, secret: parsed.secret }
      : undefined;
  } catch {
    // Storage that refuses or holds something else: there is no attempt to resume.
    return undefined;
  }
}

/** The token of an attempt that never got an answer, if the tab holds one. */
export function pendingAttemptToken(store: AttemptStore): string | undefined {
  return readAttempt(store)?.token;
}

/** The device secret for opening `token`: the one an unanswered attempt already used, else a new one, kept. */
export function secretForAttempt(
  store: AttemptStore,
  token: string,
  generate: () => string,
): string {
  const existing = readAttempt(store);
  if (existing !== undefined && existing.token === token) return existing.secret;
  const secret = generate();
  try {
    store.setItem(WELCOME_ATTEMPT_KEY, JSON.stringify({ token, secret }));
  } catch {
    // Without storage a retry makes a new secret and is refused as used; the code keypad is the way on.
  }
  return secret;
}

export function clearAttempt(store: AttemptStore): void {
  try {
    store.removeItem(WELCOME_ATTEMPT_KEY);
  } catch {
    // Nothing to clear.
  }
}
